import { isIosDevice } from './NetworkConnectionService';

export enum AndroidPermissionEnum {
    POST_NOTIFICATIONS = 'android.permission.POST_NOTIFICATIONS'
}

// NOTIFICATION
export function checkPermissionNotification(): Promise<boolean> {
    return new Promise((resolve, reject) => {
        const diagnostic = (window as any).cordova.plugins.diagnostic;
        diagnostic.isRemoteNotificationsEnabled(
            (enabled: boolean) => resolve(enabled),
            (error: any) => {
                console.error('Error checking notification permission:', error);
                reject(error);
            }
        );
    });
}

// Android notifications
export const requestNotificationPermissionAndroid = (): Promise<void> => {
    return new Promise((resolve) => {
        const permissionPlugin = (window.cordova as any)?.notifications_permission;
        if (!permissionPlugin) {
            console.warn('[askNotificationPermission] notifications_permission plugin not available, skipping.');
            resolve();
            return;
        }

        console.log('[askNotificationPermission] Asking for notification permission...');
        permissionPlugin.maybeAskPermission(
            (status: any) => {
                console.log('[askNotificationPermission] permissionPlugin status:', status);
                resolve();
            },
            {
                show: true,
                title: "Benachrichtigungen zulassen",
                msg: "Benachrichtigungen sind für die ordnungsgemäße Funktion dieser App unerlässlich. Bitte aktivieren Sie sie, um wichtige Benachrichtigungen zu erhalten.",
                okButton: "OK",
                cancelButton: "Nicht jetzt",
                theme: permissionPlugin.themes.Theme_DeviceDefault_Dialog_Alert,
            },
            {
                show: true,
                title: "Benachrichtigungen zulassen",
                msg: "Benachrichtigungen sind für die Kernfunktionalität erforderlich. Bitte aktivieren Sie sie in Ihren Systemeinstellungen, um weiterhin Benachrichtigungen zu erhalten.",
                okButton: "Einstellungen",
                cancelButton: "Nein",
                theme: permissionPlugin.themes.Theme_DeviceDefault_Dialog_Alert,
            }
        );
    });
};

// iOS notifications
export function requestIosPermissionNotification(): Promise<boolean> {
    return new Promise((resolve, reject) => {
        if (!isIosDevice()) {
            return resolve(false);
        }

        const diagnostic = (window as any).cordova.plugins.diagnostic;
        diagnostic.requestRemoteNotificationsAuthorization(
            (status: string) => {
                // success status is usually just a string array with granted types
                resolve(true);
            },
            (error: any) => {
                console.error('Error requesting iOS notification permission:', error);
                reject(error);
            },
            ['alert', 'badge', 'sound'] // iOS notification types
        );
    });
}

// AUDIO
export function checkPermissionMicrophone(): Promise<boolean> {
    return new Promise((resolve, reject) => {
        const diagnostic = (window as any).cordova.plugins.diagnostic;
        diagnostic.getMicrophoneAuthorizationStatus(
            (status: string) => {
                resolve(status === diagnostic.permissionStatus.GRANTED);
            },
            (error: any) => {
                console.error('Error checking microphone permission:', error);
                reject(error);
            }
        );
    });
}
export function requestPermissionMicrophone(): Promise<boolean> {
    return new Promise((resolve, reject) => {
        const diagnostic = (window as any).cordova.plugins.diagnostic;
        diagnostic.requestMicrophoneAuthorization(
            (status: string) => {
                resolve(status === diagnostic.permissionStatus.GRANTED);
            },
            (error: any) => {
                console.error('Error requesting microphone permission:', error);
                reject(error);
            }
        );
    });
}

// LOCATION
export function checkPermissionLocation(): Promise<boolean> {
    return new Promise((resolve, reject) => {
        const diagnostic = (window as any).cordova.plugins.diagnostic;
        diagnostic.getLocationAuthorizationStatus(
            (status: string) => {
                resolve(
                    status === diagnostic.permissionStatus.GRANTED ||
                    status === diagnostic.permissionStatus.GRANTED_WHEN_IN_USE ||
                    status === diagnostic.permissionStatus.GRANTED_ALWAYS
                );
            },
            (error: any) => {
                console.error('Error checking location permission:', error);
                reject(error);
            }
        );
    });
}
export function requestPermissionLocation(): Promise<boolean> {
    return new Promise((resolve, reject) => {
        const diagnostic = (window as any).cordova.plugins.diagnostic;
        diagnostic.requestLocationAuthorization(
            (status: string) => {
                resolve(
                    status === diagnostic.permissionStatus.GRANTED ||
                    status === diagnostic.permissionStatus.GRANTED_WHEN_IN_USE ||
                    status === diagnostic.permissionStatus.GRANTED_ALWAYS
                );
            },
            (error: any) => {
                console.error('Error requesting location permission:', error);
                reject(error);
            },
            diagnostic.locationAuthorizationMode.WHEN_IN_USE
        );
    });
}

export async function checkWebPermission(permission: 'camera' | 'microphone'): Promise<boolean> {
    try {
        switch (permission) {
            case 'camera':
            case 'microphone':
                const constraints = permission === 'camera'
                    ? { video: true }
                    : { audio: true };

                await navigator.mediaDevices.getUserMedia(constraints);
                return true;

            default:
                return false;
        }
    } catch (err) {
        console.error(err);
        return false;
    }
}
